/**
 * Public Report Types
 * Types for public issue reporting via QR codes
 *
 * Mirrors mf-functions/functions/src/publicReport/types.ts
 */

// ============================================================================
// Data Models
// ============================================================================

/**
 * Public report token stored in publicReportTokens/{token}
 */
export interface PublicReportToken {
    /** 12-character random token from an unambiguous alphabet, also used as database key */
    token: string;
    /** Building this token grants access to */
    buildingKey: string;
    /** Admin UID who created this token */
    createdBy: string;
    /** When created (YYYY-MM-DD hh:mm:ss Stockholm) */
    createdAt: string;
    /** When revoked (soft delete) */
    revokedAt?: string;
    /** Put up where non-employees may scan it; absent = internal. Set only at creation */
    publicPlacement?: true;
    /** Admin UID who acknowledged the customer's responsibility for personal data */
    acknowledgedBy?: string;
    /** When acknowledged (YYYY-MM-DD hh:mm:ss Stockholm) */
    acknowledgedAt?: string;
}

/**
 * The customer's privacy policy, shown on the report page of public codes.
 * Instance-wide, stored at config/publicReportPrivacy. At least one of url/text.
 */
export interface PublicReportPrivacyPolicy {
    /** Link to the customer's own policy page (https) */
    url?: string;
    /** Policy as plain text, line breaks kept */
    text?: string;
    /** Admin UID who last updated */
    updatedBy?: string;
    /** When last updated (YYYY-MM-DD hh:mm:ss Stockholm) */
    updatedAt?: string;
}

/** What the report page receives of the policy */
export type PublicPrivacyPolicy = Pick<PublicReportPrivacyPolicy, 'url' | 'text'>;

/**
 * Global branding settings for public report PDFs
 */
export interface PublicReportBranding {
    /** Firebase Storage download URL for logo */
    logoUrl?: string;
    /** Storage path for deletion reference */
    logoStoragePath?: string;
    /** Admin UID who last updated */
    updatedBy?: string;
    /** When last updated (YYYY-MM-DD hh:mm:ss Stockholm) */
    updatedAt?: string;
}

/**
 * Photo metadata for public report submissions
 */
export interface PublicReportPhoto {
    /** Unique key for this photo */
    key: string;
    /** Storage path where photo is stored */
    storagePath: string;
    /** Image width in pixels */
    width: number;
    /** Image height in pixels */
    height: number;
    /** File size in bytes */
    fileSize: number;
}

// ============================================================================
// API Request Types
// ============================================================================

/**
 * Request to create a token for a building (admin only)
 */
export interface CreateTokenRequest {
    action: 'createToken';
    buildingKey: string;
    /** The code will be put up where non-employees may scan it */
    publicPlacement?: boolean;
    /** Required true when publicPlacement is set */
    acknowledged?: boolean;
}

/**
 * Request to revoke a token (admin only)
 */
export interface RevokeTokenRequest {
    action: 'revokeToken';
    token: string;
}

/**
 * Request to get active token for a building (admin only)
 */
export interface GetTokenForBuildingRequest {
    action: 'getTokenForBuilding';
    buildingKey: string;
}

/**
 * Request to get all active tokens across all buildings (admin only)
 */
export interface GetAllActiveTokensRequest {
    action: 'getAllActiveTokens';
}

/**
 * Active token with building information for admin list view
 */
export interface ActiveTokenWithBuilding {
    /** The token string */
    token: string;
    /** Building key this token is for */
    buildingKey: string;
    /** Building name for display */
    buildingName: string;
    /** Building address for display */
    buildingAddress?: string;
    /** Admin UID who created this token */
    createdBy: string;
    /** When created (YYYY-MM-DD hh:mm:ss Stockholm) */
    createdAt: string;
    /** Full report URL for QR code generation */
    reportUrl: string;
    /** Placed where non-employees may scan it */
    publicPlacement: boolean;
}

/**
 * Request to validate a token and get building info (public)
 */
export interface ValidateTokenRequest {
    action: 'validateToken';
    token: string;
}

/**
 * Request to get a signed upload URL for a photo (public)
 */
export interface GetUploadUrlRequest {
    action: 'getUploadUrl';
    token: string;
    issueKey: string;
    fileName: string;
    mimeType: string;
}

/**
 * Request to submit a public issue report (public)
 */
export interface SubmitReportRequest {
    action: 'submitReport';
    token: string;
    issueKey: string;
    description: string;
    photos?: PublicReportPhoto[];
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Response from createToken
 */
export interface CreateTokenResponse {
    success: true;
    token: string;
    reportUrl: string;
}

/**
 * Response from revokeToken
 */
export interface RevokeTokenResponse {
    success: true;
}

/**
 * Response from getTokenForBuilding
 */
export interface GetTokenForBuildingResponse {
    success: true;
    token: PublicReportToken | null;
    reportUrl: string | null;
}

/**
 * Response from getAllActiveTokens
 */
export interface GetAllActiveTokensResponse {
    success: true;
    tokens: ActiveTokenWithBuilding[];
}

/**
 * Response from validateToken
 */
export interface ValidateTokenResponse {
    success: true;
    buildingName: string;
    buildingAddress?: string;
    /** Placed where non-employees may scan it */
    publicPlacement: boolean;
    /** The customer's policy, only for public codes; null when none is set */
    privacyPolicy: PublicPrivacyPolicy | null;
}

/**
 * Response from getUploadUrl
 */
export interface GetUploadUrlResponse {
    success: true;
    uploadUrl: string;
    key: string;
    storagePath: string;
}

/**
 * Response from submitReport
 */
export interface SubmitReportResponse {
    success: true;
    message: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Database path for branding config
 */
export const PUBLIC_REPORT_BRANDING_PATH = "config/publicReportBranding";

/**
 * Database path for the customer's privacy policy
 */
export const PUBLIC_REPORT_PRIVACY_PATH = "config/publicReportPrivacy";

/** Max length of the policy text (mirrors database.rules.json) */
export const MAX_PRIVACY_POLICY_TEXT_LENGTH = 20000;

/**
 * Validation limits
 */
export const LIMITS = {
    MIN_DESCRIPTION_LENGTH: 10,
    MAX_DESCRIPTION_LENGTH: 2000,
    MAX_PHOTOS: 5,
    MAX_PHOTO_SIZE_BYTES: 10 * 1024 * 1024, // 10 MB
} as const;

/**
 * Allowed MIME types for photo uploads
 */
export const ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
] as const;
